<template>
    <div class="validation-editor">
        <div class="validation-editor__presets">
            <label class="validation-editor__presets-label">
                {{ lang('fields::fields.field-editor.validation_rules') }}
            </label>
            <div v-if="loading" class="validation-editor__presets-list">
                <span class="validation-editor__loading">...</span>
            </div>
            <div v-else class="validation-editor__presets-list">
                <button
                    v-for="rule in availableRules"
                    :key="rule.id"
                    type="button"
                    class="validation-editor__preset-btn"
                    :class="[isRuleSelected(rule.id) ? 'validation-editor__preset-btn--active' : '']"
                    @click="toggleRule(rule.id)"
                >
                    {{ rule.name }}
                </button>
            </div>
        </div>

        <div v-if="showMinMax" class="validation-editor__fields">
            <!-- Min -->
            <Field
                v-model="localValidation.min"
                :field="{
                    name: lang('fields::fields.field-editor.validation_min'),
                    slug: 'min',
                    type: 'number',
                    adminComponent: 'AdminNumber',
                    placeholder: minPlaceholder,
                }"
                label-position="top"
                @change="emitUpdate"
            />

            <!-- Max -->
            <Field
                v-model="localValidation.max"
                :field="{
                    name: lang('fields::fields.field-editor.validation_max'),
                    slug: 'max',
                    type: 'number',
                    adminComponent: 'AdminNumber',
                    placeholder: maxPlaceholder,
                }"
                label-position="top"
                @change="emitUpdate"
            />
        </div>

        <!-- Regex -->
        <div v-if="showRegex">
            <Field
                v-model="localValidation.regex"
                :field="{
                    name: lang('fields::fields.field-editor.validation_regex'),
                    slug: 'regex',
                    type: 'text',
                    adminComponent: 'AdminText',
                    placeholder: '/^[a-z]+$/i',
                }"
                label-position="top"
                @change="emitUpdate"
            />
        </div>

        <!-- Custom Error Message -->
        <div>
            <Field
                v-model="localValidation.customError"
                :field="{
                    name: lang('fields::fields.field-editor.validation_custom_error'),
                    slug: 'customError',
                    type: 'text',
                    adminComponent: 'AdminText',
                    placeholder: lang('fields::fields.field-editor.validation_custom_error_placeholder'),
                }"
                label-position="top"
                @change="emitUpdate"
            />
        </div>

        <!-- Preview of validation rules -->
        <div v-if="hasAnyRules" class="validation-editor__preview">
            <label class="validation-editor__preview-label">
                {{ lang('fields::fields.field-editor.validation_preview') }}
            </label>
            <div class="validation-editor__preview-code">
                <code>{{ validationPreview }}</code>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { lang } from '@/lang/composable';
import axios from 'axios';

interface ValidationData {
    min?: number | null;
    max?: number | null;
    regex?: string | null;
    customError?: string | null;
    preset?: string | null;
    rules?: string[];
}

interface ValidationRule {
    id: string;
    name: string;
}

interface Props {
    validation: ValidationData;
    fieldType: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    (event: 'update:validation', validation: ValidationData): void;
}>();

const localValidation = ref<ValidationData>({ ...props.validation });
const availableRules = ref<ValidationRule[]>([]);
const loading = ref(true);

// Migrate old preset to rules on init
if (localValidation.value.preset && (!localValidation.value.rules || localValidation.value.rules.length === 0)) {
    localValidation.value.rules = [localValidation.value.preset];
}

if (!localValidation.value.rules) {
    localValidation.value.rules = [];
}

watch(
    () => props.validation,
    (newValidation) => {
        localValidation.value = { ...newValidation };

        // Ensure rules array exists and migrate preset
        if (!localValidation.value.rules) {
            localValidation.value.rules = [];
        }

        if (localValidation.value.preset && localValidation.value.rules.length === 0) {
            localValidation.value.rules = [localValidation.value.preset];
        }
    },
    { deep: true },
);

const fetchRules = async (fieldType?: string) => {
    loading.value = true;

    try {
        const { data } = await axios.get(route('fields.validation-rules'), {
            params: { fieldType },
        });
        availableRules.value = data.rules;
    } catch {
        availableRules.value = [];
    } finally {
        loading.value = false;
    }
};

onMounted(() => {
    fetchRules(props.fieldType);
});

watch(
    () => props.fieldType,
    (newFieldType) => {
        fetchRules(newFieldType);
    },
);

const showMinMax = computed(() => {
    return ['text', 'textarea', 'number', 'password'].includes(props.fieldType);
});

const showRegex = computed(() => {
    return ['text', 'textarea', 'password'].includes(props.fieldType);
});

const minPlaceholder = computed(() => {
    return props.fieldType === 'number'
        ? lang('fields::fields.field-editor.min_value')
        : lang('fields::fields.field-editor.min_length');
});

const maxPlaceholder = computed(() => {
    return props.fieldType === 'number'
        ? lang('fields::fields.field-editor.max_value')
        : lang('fields::fields.field-editor.max_length');
});

const hasAnyRules = computed(() => {
    return (
        localValidation.value.min !== null ||
        localValidation.value.max !== null ||
        localValidation.value.regex ||
        (localValidation.value.rules && localValidation.value.rules.length > 0)
    );
});

const validationPreview = computed(() => {
    const parts: string[] = [];

    if (localValidation.value.rules && localValidation.value.rules.length > 0) {
        parts.push(...localValidation.value.rules);
    }

    if (localValidation.value.min !== null && localValidation.value.min !== undefined) {
        parts.push(`min:${localValidation.value.min}`);
    }

    if (localValidation.value.max !== null && localValidation.value.max !== undefined) {
        parts.push(`max:${localValidation.value.max}`);
    }

    if (localValidation.value.regex) {
        parts.push(`regex:${localValidation.value.regex}`);
    }

    return parts.join(' | ');
});

const isRuleSelected = (ruleId: string): boolean => {
    return localValidation.value.rules?.includes(ruleId) ?? false;
};

const toggleRule = (ruleId: string) => {
    if (!localValidation.value.rules) {
        localValidation.value.rules = [];
    }

    const index = localValidation.value.rules.indexOf(ruleId);

    if (index === -1) {
        localValidation.value.rules.push(ruleId);
    } else {
        localValidation.value.rules.splice(index, 1);
    }

    // Keep preset in sync for backward compat (first selected rule or null)
    localValidation.value.preset = localValidation.value.rules.length > 0
        ? localValidation.value.rules[0]
        : null;

    emitUpdate();
};

const emitUpdate = () => {
    emit('update:validation', { ...localValidation.value });
};
</script>
